import React, { useRef, useState } from 'react';
import { InsuranceOffer, ProductVariant } from 'types/Product';
import { InsuranceContent, InsuranceValue } from 'types/ProductResponseType';
import Button from 'components/Button/Button';
import { Tabs, TabPanel } from '@tvg/fuse/tabs';
import { Typography } from '@tvg/fuse/typography';
import { Link } from '@tvg/fuse/link';
import { Alert } from '@tvg/fuse/alert';
import { Modal } from '@tvg/fuse/modal';
import { enableMonthlyProtectInsuranceTempOffer } from 'constants/promotion-voucher-constants';
import { getSite } from 'context/SiteContext';
import { InsuraceDescription } from '../InsuranceDescription/InsuranceDescription';
import { DialogProps } from '../Shared/SharedProps';
import styles from './CheckItsRightDialog.module.css';

enum TextTypes {
  bold = 'bold',
  regular = 'regular',
  link = 'link',
}

enum InsuranceTypes {
  monthly = 'Monthly Insurance',
  yearly = 'Fixed term insurance',
}
interface Props extends DialogProps {
  options?: InsuranceOffer[];
  open: boolean;
  selectedSkuInsuranceContent: InsuranceContent;
  handleSelectedInsurance: (option: InsuranceOffer | null) => void;
  selectedInsurance?: InsuranceOffer | string | null;
  findOutMoreView: boolean;
  handleContinueWithoutInsurance: () => void;
  onAddInsuranceClick: () => void;
  selectedSku: ProductVariant;
}

export function CheckItsRightDialog({
  selectedSku,
  options,
  onClose,
  open,
  selectedSkuInsuranceContent,
  handleSelectedInsurance,
  selectedInsurance,
  findOutMoreView,
  handleContinueWithoutInsurance,
  onAddInsuranceClick,
}: Props) {
  const [insuranceType, setInsuranceType] = useState(InsuranceTypes.monthly);
  const scrollAnchor = useRef<HTMLDivElement>(null);

  const isScroll =
    scrollAnchor.current &&
    scrollAnchor.current.parentElement &&
    scrollAnchor.current.parentElement.scrollHeight > scrollAnchor.current.parentElement.clientHeight;
  const { checkIfRightMonthly, checkIfRightYearly, covered, notCovered, value, valueAfter } =
    selectedSkuInsuranceContent.insuranceContent;
  const showTabs = checkIfRightMonthly && checkIfRightYearly && findOutMoreView;
  const isMonthly =
    selectedInsurance &&
    typeof selectedInsurance !== 'string' &&
    !findOutMoreView &&
    selectedInsurance.offer.productDescription.split(' ').includes('Monthly');

  const renderContent = (content: InsuranceValue[][]) =>
    content.map((paragraph, paragraphIdx) => (
      <Typography key={`insurance-paragraph-${paragraphIdx}`} size="m1" tag="p" type="paragraph">
        {paragraph.map((line, lineIdx) => {
          if (line.type === TextTypes.bold) {
            if (line.text?.includes('By adding insurance')) {
              return (
                <div key={`insurance-line-${paragraphIdx}-${lineIdx}`} className={styles.dialogNotification}>
                  <Typography size="m1" color="var(--color-global-black)" tag="span" type="heading">
                    {line.text}
                    <br />
                  </Typography>
                </div>
              );
            }
            return (
              <Typography
                key={`insurance-line-${paragraphIdx}-${lineIdx}`}
                size="m1"
                color="var(--color-global-black)"
                tag="span"
                type="heading"
              >
                {line.text}
                <br />
              </Typography>
            );
          }
          if (line.type === TextTypes.regular) {
            return (
              <Typography key={`insurance-line-${paragraphIdx}-${lineIdx}`} size="m1" tag="span" type="paragraph">
                {line.text}
              </Typography>
            );
          }

          if (line.type === TextTypes.link) {
            return (
              <Link
                key={`insurance-line-${paragraphIdx}-${lineIdx}`}
                className={styles.link}
                target="_blank"
                rel="noopener noreferrer"
                href={line.url}
                underline
              >
                <Typography size="m1" tag="span" type="paragraph" color="var(--color-global-black)">
                  {` ${line.text} `}
                </Typography>
              </Link>
            );
          }

          return null;
        })}
      </Typography>
    ));

  const getTitle = () => {
    if (!findOutMoreView && selectedInsurance && typeof selectedInsurance !== 'string') {
      const shouldAddMonthlyTitle = selectedInsurance.offer.termDescription.includes('Monthly');
      return `${selectedInsurance?.offer?.termDescription} £${selectedInsurance?.offer?.sellingPrice}${shouldAddMonthlyTitle ? ' a month' : ''}`;
    }
    return (
      selectedSkuInsuranceContent?.insuranceContent.pdpInsuranceDescription[0][0].text ||
      selectedSkuInsuranceContent?.insuranceContent.pdpInsuranceDescription[0][0].title
    )?.replace('Insurance', '');
  };

  const resetStateAfterAnimationCompleted = () => {
    setTimeout(() => setInsuranceType(InsuranceTypes.monthly), 200);
  };

  const handleModalClose = () => {
    onClose();
    if (insuranceType !== InsuranceTypes.monthly) {
      resetStateAfterAnimationCompleted();
    }
  };

  const handleAddInsuranceClick = (insuranceOffer: InsuranceOffer) => {
    handleSelectedInsurance(insuranceOffer);
    onAddInsuranceClick();
    resetStateAfterAnimationCompleted();
  };

  const handleNoInsurance = () => {
    handleContinueWithoutInsurance();
    resetStateAfterAnimationCompleted();
  };

  const getInsuraceButtons = () =>
    selectedSku?.insuranceOffers?.map((insuranceOffer) => {
      if (
        showTabs &&
        insuranceOffer?.offer.termDescription === insuranceType &&
        insuranceType === InsuranceTypes.monthly
      ) {
        return (
          <div className={styles.selectInsuranceButtonContainer}>
            <Button
              id="insurance-add-button"
              onClick={() => handleAddInsuranceClick(insuranceOffer)}
              dataTestId="check-its-right-add-button"
            >
              {`${insuranceOffer?.offer.termDescription} £${insuranceOffer?.offer.sellingPrice} a month`}
            </Button>
          </div>
        );
      }
      if (
        showTabs &&
        insuranceOffer?.offer.termDescription !== InsuranceTypes.monthly &&
        insuranceType === InsuranceTypes.yearly
      ) {
        return (
          <div className={styles.selectInsuranceButtonContainer}>
            <Button
              id="insurance-add-button"
              onClick={() => handleAddInsuranceClick(insuranceOffer)}
              dataTestId="check-its-right-add-button"
            >
              {`${insuranceOffer?.offer.termDescription} £${insuranceOffer?.offer.sellingPrice}`}
            </Button>
          </div>
        );
      }

      if (!showTabs) {
        return (
          <div className={styles.selectInsuranceButtonContainer}>
            <Button
              id="insurance-add-button"
              onClick={() => handleAddInsuranceClick(insuranceOffer)}
              dataTestId="check-its-right-add-button"
            >
              {`${insuranceOffer?.offer.termDescription} £${insuranceOffer?.offer.sellingPrice}`}
            </Button>
          </div>
        );
      }

      return null;
    });

  const handleTypeOfInsuranceChange = (e: any) => {
    const label = (e?.target as HTMLElement | undefined)?.textContent?.trim();
    if (label === InsuranceTypes.monthly) {
      setInsuranceType(InsuranceTypes.monthly);
    }

    if (label === InsuranceTypes.yearly) {
      setInsuranceType(InsuranceTypes.yearly);
    }
  };

  const getMonthlyInsuranceTemporaryText = () => (
    <Typography className={styles.monthlyTempText} size="m1" color="var(--color-global-black)" tag="h3" type="heading">
      *Get 6 months FREE insurance when you buy Protect - Repair Monthly insurance with this item and add to your Very
      Pay credit account. This offer will not be displayed at basket but will be visible on your account after purchase.
      The monthly premium and a refund of it will show on your statement each month for the first 6 months. Cancel any
      time in your online account if you don’t want the policy to continue. To qualify for insurance each month, your
      Very Pay account must remain open and not be in arrears. Representative 44.9% APR variable.
    </Typography>
  );

  return options ? (
    <Modal
      className={styles.modal}
      title={getTitle()}
      titleTag="h2"
      dataTestId="check-its-right-dialog"
      srLabelBackButton="check_its_right_back_button"
      srLabelCloseButton="check its right_dialog"
      open={open}
      zIndex="20"
      onClose={handleModalClose}
      key={`check-its-right-dialog-${findOutMoreView}`}
    >
      <div data-testid="check-if-its-right-scroll-anchor" ref={scrollAnchor} />
      {isScroll && (
        <div className={styles.scrollInfoAlert}>
          <Alert
            variant="information"
            size="small"
            message="Scroll down to read and confirm if this insurance is right for you."
            dataTestId="insurance-check-its-right-info"
          />
        </div>
      )}
      {isMonthly &&
        enableMonthlyProtectInsuranceTempOffer &&
        getSite() === 'very' &&
        getMonthlyInsuranceTemporaryText()}

      {value && renderContent(value)}
      {covered && notCovered && (
        <InsuraceDescription
          coverdTitle={covered.text}
          notCoveredTitle={notCovered.text}
          covered={covered.bulletText}
          notCovered={notCovered.bulletText}
          withDescription={false}
        />
      )}
      {showTabs && (
        <div className={styles.bottomDescriptionContainer}>
          <Tabs
            className={[styles.tabs, 'insurance-fuse-tabs'].filter(Boolean).join(' ')}
            initialTab={1}
            tabListAriaLabel="Insurance Tabs"
            onChange={handleTypeOfInsuranceChange}
          >
            <TabPanel label={InsuranceTypes.monthly}>{renderContent(checkIfRightMonthly)}</TabPanel>

            <TabPanel label={InsuranceTypes.yearly}>{renderContent(checkIfRightYearly)}</TabPanel>
          </Tabs>
        </div>
      )}
      {!showTabs && !isMonthly && checkIfRightYearly && renderContent(checkIfRightYearly)}
      {!showTabs && isMonthly && checkIfRightMonthly && renderContent(checkIfRightMonthly)}
      {valueAfter && renderContent(valueAfter)}

      <Typography className={styles.typography} color="var(--color-global-black)" size="m1" tag="p">
        Choose an option below
      </Typography>
      <div className={styles.addToBasketDialogContainer}>
        {!findOutMoreView && selectedInsurance && typeof selectedInsurance !== 'string' ? (
          <div className={styles.selectInsuranceButtonContainer}>
            <Button id="insurance-add-button" onClick={onAddInsuranceClick} dataTestId="check-its-right-add-button">
              {`${selectedInsurance?.offer.termDescription} £${selectedInsurance?.offer.sellingPrice} ${selectedInsurance?.offer.termDescription === InsuranceTypes.monthly ? 'a month' : ''}`}
            </Button>
          </div>
        ) : (
          <>{getInsuraceButtons()}</>
        )}
        <Button variant="tertiary" dataTestId="continue-without-insurance-button" onClick={handleNoInsurance}>
          Continue without insurance
        </Button>
      </div>
    </Modal>
  ) : null;
}

export default CheckItsRightDialog;
